/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React from 'react';
import PropTypes from "prop-types";
import { keysOf } from '../common';
import classNames from 'classnames';
var typeToClassNameMap = {
  regular: 'euiCommentEvent--regular',
  update: 'euiCommentEvent--update'
};
export var TYPES = keysOf(typeToClassNameMap);
export var EuiCommentEvent = ({
  children,
  className,
  username,
  timestamp,
  type = 'regular',
  event,
  actions
}) => {
  const classes = classNames('euiCommentEvent', typeToClassNameMap[type], className);
  const isFigure = type === 'regular' || type === 'update' && typeof children !== 'undefined';
  const Element = isFigure ? 'figure' : 'div';
  const HeaderElement = isFigure ? 'figcaption' : 'div';
  return <Element className={classes}>
      <HeaderElement className="euiCommentEvent__header">
        <div className="euiCommentEvent__headerData">
          <div className="euiCommentEvent__headerUsername">{username}</div>
          <div className="euiCommentEvent__headerEvent">{event}</div>
          {timestamp ? <div className="euiCommentEvent__headerTimestamp">
              <time>{timestamp}</time>
            </div> : undefined}
        </div>
        {actions ? <div className="euiCommentEvent__headerActions">{actions}</div> : undefined}
      </HeaderElement>
      {children ? <div className="euiCommentEvent__body">{children}</div> : undefined}
    </Element>;
};
EuiCommentEvent.propTypes = {
  /**
     * Author of the comment. Display a small icon or avatar with it if needed.
     */
  username: PropTypes.node.isRequired,

  /**
     * Time of occurrence of the event. Its format is set on the consumer's side
     */
  timestamp: PropTypes.node,

  /**
     * Describes the event that took place
     */
  event: PropTypes.node,

  /**
     * Custom actions that the user can perform from the comment's header
     */
  actions: PropTypes.node,

  /**
     * Use "update" when the comment is primarily showing info about actions that the user or the system has performed (e.g. "user1 edited a case").
     */
  type: PropTypes.oneOf(["regular", "update"]),
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  // @ts-ignore
  EuiCommentEvent.displayName = "EuiCommentEvent"; // @ts-ignore

  EuiCommentEvent.__docgenInfo = {
    "description": "",
    "displayName": "EuiCommentEvent",
    "props": {
      "username": {
        "defaultValue": null,
        "description": "Author of the comment. Display a small icon or avatar with it if needed.",
        "name": "username",
        "required": true,
        "type": {
          "name": "ReactNode"
        }
      },
      "timestamp": {
        "defaultValue": null,
        "description": "Time of occurrence of the event. Its format is set on the consumer's side",
        "name": "timestamp",
        "required": false,
        "type": {
          "name": "ReactNode"
        }
      },
      "event": {
        "defaultValue": null,
        "description": "Describes the event that took place",
        "name": "event",
        "required": false,
        "type": {
          "name": "ReactNode"
        }
      },
      "actions": {
        "defaultValue": null,
        "description": "Custom actions that the user can perform from the comment's header",
        "name": "actions",
        "required": false,
        "type": {
          "name": "ReactNode"
        }
      },
      "type": {
        "defaultValue": {
          value: "regular"
        },
        "description": "Use \"update\" when the comment is primarily showing info about actions that the user or the system has performed (e.g. \"user1 edited a case\").",
        "name": "type",
        "required": false,
        "type": {
          "name": "\"regular\" | \"update\" | undefined"
        }
      },
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-label": {
        "defaultValue": null,
        "description": "",
        "name": "aria-label",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "data-test-subj": {
        "defaultValue": null,
        "description": "",
        "name": "data-test-subj",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/comment_list/comment_event.tsx#EuiCommentEvent"] = {
      docgenInfo: EuiCommentEvent.__docgenInfo,
      name: "EuiCommentEvent",
      path: "src/components/comment_list/comment_event.tsx#EuiCommentEvent"
    };
} catch (__react_docgen_typescript_loader_error) {}